import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./QuizUi.css";
import fiftyFifty from "../../img/fifty_fifty.png";
import doubleDip from "../../img/double_dip.png";
import flip from "../../img/flip.png";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { getLobbyGameData } from "../../functions/getLobbyGameData";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { saveLobbyGameProgress } from "../../functions/saveLobbyGameProgress";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { fireFeedbackConfetti } from "../../functions/fireFeedbackConfetti";

const ANSWER_GRADIENT = [
  "quiz-ui__answer--warm",
  "quiz-ui__answer--cool",
  "quiz-ui__answer--aqua",
  "quiz-ui__answer--magenta",
];

function normalizeBool(v) {
  if (typeof v === "boolean") return v;
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return false;
  return s === "true" || s === "1" || s === "yes" || s === "y";
}

function seedFromString(str) {
  let h = 2166136261;
  const s = String(str ?? "");
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function rand() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffleWithSeed(arr, seedStr) {
  const a = Array.isArray(arr) ? [...arr] : [];
  const rand = mulberry32(seedFromString(seedStr));
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function fmtTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function normalizeRow(row) {
  const opts = [
    String(row?.option_one ?? "").trim(),
    String(row?.option_two ?? "").trim(),
    String(row?.option_three ?? "").trim(),
    String(row?.option_four ?? "").trim(),
  ];
  const rawAid = Number.parseInt(String(row?.answer_id ?? "1").trim(), 10);
  const answerId = Number.isFinite(rawAid) && rawAid >= 1 && rawAid <= 4 ? rawAid : 1;
  return {
    id: row?.id != null ? String(row.id) : `${row?.question_name}-${Math.random()}`,
    question: String(row?.question_name ?? "").trim(),
    options: opts,
    answerId,
    correctText: String(opts[answerId - 1] ?? "").trim(),
  };
}

function quizLifelinesStorageKey(userId, lobby, stageId) {
  return `quiz_mc_lifelines_used_v1:${String(userId ?? "")}:${String(lobby ?? "")}:${String(stageId ?? "")}`;
}

function readQuizLifelinesUsed(key) {
  if (!key || typeof sessionStorage === "undefined") return {};
  try {
    const o = JSON.parse(sessionStorage.getItem(key) || "{}");
    return o && typeof o === "object" ? o : {};
  } catch {
    return {};
  }
}

function writeQuizLifelinesUsed(key, patch) {
  if (!key) return;
  const cur = readQuizLifelinesUsed(key);
  try {
    sessionStorage.setItem(key, JSON.stringify({ ...cur, ...patch }));
  } catch {
    /* ignore */
  }
}

function clearQuizLifelinesUsed(key) {
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export default function QuizUi() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || localStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);

  const isAnchorMode = useMemo(() => {
    if (location.state?.isAnchorMode) return true;
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || localStorage.getItem("gameSettings") || "{}");
      return cached?.isAnchorMode === true;
    } catch {
      return false;
    }
  }, [location.state?.isAnchorMode]);

  const uid =
    user?.userId ??
    user?.userid ??
    user?.id ??
    storedUserData.userId ??
    storedUserData.userid ??
    storedUserData.id;

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = user?.sessionId ?? user?.sessionid ?? storedUserData.sessionId;
    const oid = user?.organizationId ?? user?.orgId ?? user?.organizationid ?? storedUserData.organizationId;
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, storedUserData, user]);

  const lobbyNameMemo = useMemo(() => {
    const st = location.state || {};
    if (st?.lobby_name) return String(st.lobby_name);
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || localStorage.getItem("gameSettings") || "{}");
      if (cached?.lobby_name) return String(cached.lobby_name);
    } catch {
      /* ignore */
    }
    if (storedUserData?.lobby) return String(storedUserData.lobby);
    return "";
  }, [location.state, storedUserData?.lobby]);

  const lobbyPin = useMemo(() => {
    const st = location.state || {};
    if (st?.lobby_pin) return String(st.lobby_pin);
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || localStorage.getItem("gameSettings") || "{}");
      if (cached?.lobby_pin) return String(cached.lobby_pin);
    } catch {
      /* ignore */
    }
    const ln = String(lobbyNameMemo || "");
    const m = ln.match(/LOBBY-(\d{5})/i);
    if (m && m[1]) return String(m[1]);
    return "";
  }, [location.state, lobbyNameMemo]);

  const themeName = useMemo(() => {
    const st = location.state || {};
    if (st?.themeName != null && String(st.themeName).trim() !== "") return String(st.themeName).trim();
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || localStorage.getItem("gameSettings") || "{}");
      if (cached?.themeName != null && String(cached.themeName).trim() !== "") return String(cached.themeName).trim();
    } catch {
      /* ignore */
    }
    return storedUserData.themeName || user?.themeName || "guess_me";
  }, [location.state, storedUserData.themeName, user?.themeName]);

  const [activeLobbyName, setActiveLobbyName] = useState("");
  useEffect(() => {
    if (lobbyNameMemo && String(lobbyNameMemo).trim() !== "") setActiveLobbyName(String(lobbyNameMemo).trim());
  }, [lobbyNameMemo]);

  const [stageId, setStageId] = useState(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || localStorage.getItem("gameSettings") || "{}");
      return cached?.stageId != null && String(cached.stageId).trim() !== "" ? String(cached.stageId).trim() : null;
    } catch {
      return null;
    }
  });

  const reportIdRef = useRef(null);
  const reportCreatePromiseRef = useRef(null);
  const reportStartSentRef = useRef(false);
  const totalElapsedRef = useRef(0);
  const questionStartRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const timeUpHandledRef = useRef(false);
  const scoreRef = useRef(0);
  const lifelinesPersistKeyRef = useRef("");

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [flipReserve, setFlipReserve] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [pointsPerCorrect, setPointsPerCorrect] = useState(0);
  const [timeDurationMin, setTimeDurationMin] = useState(1);
  const [score, setScore] = useState(0);
  const [, setAttempts] = useState([]);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  const [remaining, setRemaining] = useState(60);
  const [selected, setSelected] = useState(() => new Set());
  const [feedback, setFeedback] = useState(null);
  const [disabled5050, setDisabled5050] = useState(() => new Set());

  const [lifelinesEnabled, setLifelinesEnabled] = useState(false);
  const [settingsFifty, setSettingsFifty] = useState(false);
  const [settingsDouble, setSettingsDouble] = useState(false);
  const [settingsFlip, setSettingsFlip] = useState(false);

  const [used50, setUsed50] = useState(false);
  const [flipSpent, setFlipSpent] = useState(false);
  const [usedDouble, setUsedDouble] = useState(false);
  const [doubleDipActive, setDoubleDipActive] = useState(false);

  const [lifelineModal, setLifelineModal] = useState(null);

  const finishLifelineModal = useCallback((confirmed) => {
    setLifelineModal((m) => {
      if (!m) return null;
      if (m.variant === "confirm") m.resolve(confirmed === true);
      else m.resolve();
      return null;
    });
  }, []);

  const lifelinesPersistKey = useMemo(() => {
    if (!uid || !activeLobbyName || !stageId) return "";
    return quizLifelinesStorageKey(uid, activeLobbyName, stageId);
  }, [uid, activeLobbyName, stageId]);

  useEffect(() => {
    lifelinesPersistKeyRef.current = lifelinesPersistKey;
  }, [lifelinesPersistKey]);

  useEffect(() => {
    if (!lifelinesPersistKey) return;
    const s = readQuizLifelinesUsed(lifelinesPersistKey);
    if (s.fifty) setUsed50(true);
    if (s.double) setUsedDouble(true);
    if (s.flip) setFlipSpent(true);
  }, [lifelinesPersistKey]);

  useEffect(() => {
    const st = location.state || {};
    const explicitBack = st.backTo != null ? String(st.backTo).trim() : "";
    if (explicitBack) {
      dispatch(setBackButtonUrl(explicitBack));
      return;
    }
    if (isAnchorMode) {
      dispatch(setBackButtonUrl("/manager-dashboard"));
      return;
    }
    dispatch(setBackButtonUrl("/configure-game-settings"));
  }, [dispatch, isAnchorMode, location.state]);

  useEffect(() => {
    if (!lifelineModal) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      finishLifelineModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lifelineModal, finishLifelineModal]);

  const isDemoBypass = useMemo(() => {
    const role = String(user?.role ?? storedUserData?.role ?? storedUserData?.user_type ?? "").trim().toLowerCase();
    const src = String(user?.source ?? storedUserData?.source ?? "").trim().toLowerCase();
    const sid = String(user?.sessionId ?? storedUserData?.sessionId ?? "").trim().toLowerCase();
    const oid = String(user?.organizationId ?? storedUserData?.organizationId ?? "").trim().toLowerCase();
    return (
      role === "demobypass" ||
      src === "demo" ||
      (sid === "demobypass" && oid === "demobypass") ||
      sid.startsWith("demo")
    );
  }, [user, storedUserData]);

  const pushGameReport = useCallback(
    async (overrides) => {
      if (isDemoBypass) return;
      if (!uid) return;
      if (!tokenForApi) return;

      const o = overrides && typeof overrides === "object" ? overrides : {};
      const pointsVal = Number.isFinite(Number(o.points)) ? Number(o.points) : 0;
      const timeSec = Number.isFinite(Number(o.time)) ? Number(o.time) : 0;
      const timeText = fmtTime(Math.max(0, Math.floor(timeSec)));

      const roleUpper = String(user?.role ?? storedUserData?.role ?? storedUserData?.user_type ?? "").trim().toUpperCase();
      const sourceUpper = String(user?.source ?? storedUserData?.source ?? "").trim().toUpperCase();
      const isGuest = roleUpper === "GUEST_USER" || sourceUpper === "GUEST_USER";

      const useRegisteredReportApi =
        (roleUpper === "ORG_USER" || sourceUpper === "ORG_USER") &&
        user?.token != null &&
        String(user.token).trim() !== "";

      const token = String(user?.token ?? storedUserData?.token ?? "").trim();

      const basePayload = {
        sessionId: user?.sessionId ?? storedUserData.sessionId,
        role: user?.role ?? storedUserData.role ?? storedUserData.user_type,
        token,
        name: (() => {
          const direct = user?.name ?? storedUserData.name;
          if (direct != null && String(direct).trim() !== "") return String(direct).trim();
          const em = user?.email ?? storedUserData.email;
          if (em != null && String(em).trim() !== "") {
            const s = String(em).trim();
            const prefix = s.includes("@") ? s.split("@")[0] : s;
            if (prefix.trim() !== "") return prefix.trim();
          }
          return "Guest";
        })(),
        email: user?.email ?? storedUserData.email ?? null,
        userId: uid,
        gameId: user?.gameId ?? storedUserData.gameId ?? "",
        organizationId: user?.organizationId ?? storedUserData.organizationId,
        points: pointsVal,
        time: timeText,
        ans: activeLobbyName ? `GUESS_ME:${String(activeLobbyName)}` : "GUESS_ME",
        user_type: user?.role ?? storedUserData.role ?? storedUserData.user_type,
        owner: user?.role ?? storedUserData.role ?? storedUserData.user_type,
        guestScoreMinimal: isGuest,
        ...(useRegisteredReportApi ? { useRegisteredReportApi: true } : {}),
      };

      let ridForSend =
        reportIdRef.current != null && String(reportIdRef.current).trim() !== "" ? String(reportIdRef.current).trim() : "";

      if (!ridForSend && !reportCreatePromiseRef.current) {
        reportCreatePromiseRef.current = (async () => {
          const created = await sendReport({ ...basePayload, points: 0, time: "00:00", reportId: null });
          const createdId = extractReportIdFromResponse(created);
          if (createdId && String(createdId).trim() !== "") {
            const clean = String(createdId).trim();
            reportIdRef.current = clean;
            try {
              await updateGameSettings({
                token: tokenForApi,
                userId: uid,
                themeName,
                reportid: clean,
                lobby_name: activeLobbyName || lobbyNameMemo || null,
              });
            } catch {
              /* ignore */
            }
            return clean;
          }
          return "";
        })().finally(() => {
          reportCreatePromiseRef.current = null;
        });
        ridForSend = await reportCreatePromiseRef.current;
      }

      try {
        const res = await sendReport({ ...basePayload, reportId: ridForSend || null });
        const newId = extractReportIdFromResponse(res);
        if (newId && String(newId).trim() !== "") {
          const clean = String(newId).trim();
          if (clean !== ridForSend) {
            reportIdRef.current = clean;
            try {
              await updateGameSettings({
                token: tokenForApi,
                userId: uid,
                themeName,
                reportid: clean,
                lobby_name: activeLobbyName || lobbyNameMemo || null,
              });
            } catch {
              /* ignore */
            }
          }
        }
      } catch (e) {
        console.warn("[QuizUi] sendReport failed", e?.response?.status, e?.response?.data ?? e?.message ?? e);
      }
    },
    [isDemoBypass, uid, tokenForApi, user, storedUserData, activeLobbyName, lobbyNameMemo, themeName]
  );

  useEffect(() => {
    const roleUpper = String(user?.role ?? storedUserData?.role ?? storedUserData?.user_type ?? "").trim().toUpperCase();
    const sourceUpper = String(user?.source ?? storedUserData?.source ?? "").trim().toUpperCase();
    const isGuest = roleUpper === "GUEST_USER" || sourceUpper === "GUEST_USER";
    if (isGuest) return;
    const rid =
      user?.reportId != null && String(user.reportId).trim() !== ""
        ? String(user.reportId).trim()
        : storedUserData?.reportId != null && String(storedUserData.reportId).trim() !== ""
          ? String(storedUserData.reportId).trim()
          : null;
    if (rid) reportIdRef.current = rid;
  }, [user?.reportId, storedUserData?.reportId, user?.role, user?.source, storedUserData?.role, storedUserData?.user_type, storedUserData?.source]);

  useEffect(() => {
    if (!tokenForApi || !uid) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const stage = await updateGameSettings({
          token: tokenForApi,
          userId: uid,
          ...(stageId ? { stageId } : {}),
          themeName,
          show_question: true,
          lobby_name: lobbyNameMemo || storedUserData?.lobby || null,
          ...(lobbyPin ? { lobby_pin: lobbyPin } : {}),
          email: storedUserData?.email ?? user?.email ?? null,
          fullName: storedUserData?.name ?? user?.name ?? storedUserData?.fullName ?? null,
          employeeId: storedUserData?.employeeId ?? user?.employeeId ?? null,
          user_type: storedUserData?.role ?? storedUserData?.user_type ?? user?.role ?? user?.user_type ?? null,
        });
        if (cancelled) return;
        if (stage?.stageId != null && String(stage.stageId).trim() !== "") {
          const nextStageId = String(stage.stageId).trim();
          setStageId(nextStageId);
          try {
            const prev = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
            sessionStorage.setItem("gameSettings", JSON.stringify({ ...prev, stageId: nextStageId }));
          } catch {
            /* ignore */
          }
        }
        const resolvedLobby =
          lobbyNameMemo && String(lobbyNameMemo).trim() !== ""
            ? String(lobbyNameMemo).trim()
            : stage?.lobby_name != null
              ? String(stage.lobby_name).trim()
              : "";
        if (!resolvedLobby) throw new Error("Missing lobby_name");
        setActiveLobbyName(resolvedLobby);

        const data = await getLobbyGameData({
          token: tokenForApi,
          userId: uid,
          themeName,
          lobby_name: resolvedLobby,
          ...(lobbyPin ? { lobby_pin: lobbyPin } : {}),
        });
        if (cancelled) return;

        const stageStatus = String(data?.status || "").trim().toLowerCase();
        if (stageStatus === "completed") {
          navigate("/thankyou", { replace: true, state: { lobby_name: resolvedLobby, isAnchorMode: isAnchorMode === true } });
          return;
        }

        const dur = Number(data?.time_duration);
        setTimeDurationMin(Number.isFinite(dur) && dur > 0 ? dur : 1);
        const pts = Number(data?.points);
        setPointsPerCorrect(Number.isFinite(pts) ? pts : 0);

        const shuffleOn = normalizeBool(stage?.shuffle_questions ?? data?.shuffle_questions);
        const le = normalizeBool(data?.lifelines_enabled);
        const lf = normalizeBool(data?.lifeline_fifty_fifty);
        const ld = normalizeBool(data?.lifeline_double_dip);
        const lflip = normalizeBool(data?.lifeline_flip);
        setLifelinesEnabled(le);
        setSettingsFifty(le && lf);
        setSettingsDouble(le && ld);
        setSettingsFlip(le && lflip);

        let raw = Array.isArray(data?.questions) ? data.questions.map(normalizeRow).filter((q) => q.question && q.options.every(Boolean)) : [];

        let reserve = null;
        if (le && lflip && raw.length >= 2) {
          reserve = raw[raw.length - 1];
          raw = raw.slice(0, -1);
        }

        const ordered = shuffleOn ? shuffleWithSeed(raw, `${resolvedLobby}|${uid}`) : raw;
        setQuestions(ordered);
        setFlipReserve(reserve);

        try {
          if (Array.isArray(data?.questions) && data.questions.length > 0) {
            await updateGameSettings({
              token: tokenForApi,
              userId: uid,
              ...(stageId ? { stageId } : {}),
              themeName,
              questions_no: String(ordered.length),
              lobby_name: resolvedLobby,
              ...(lobbyPin ? { lobby_pin: lobbyPin } : {}),
            });
          }
        } catch {
          /* ignore */
        }

        setCurrentIndex(0);
        setScore(0);
        setAttempts([]);
        totalElapsedRef.current = 0;
        reportStartSentRef.current = false;

        if (!ordered.length) {
          await Swal.fire({ icon: "error", title: "No questions", text: "This lobby has no playable questions yet." });
          navigate("/login", { replace: true });
        }
      } catch (e) {
        if (!cancelled) await Swal.fire({ icon: "error", title: "Error", text: e?.message || "Failed to load game" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenForApi, uid, lobbyNameMemo, themeName, lobbyPin, storedUserData, navigate, isAnchorMode, stageId, user]);

  const perQuestionTotalSec = useMemo(
    () => Math.max(1, Math.round(Number(timeDurationMin) * 60)),
    [timeDurationMin]
  );

  const current = questions[currentIndex] || null;

  useEffect(() => {
    if (loading) return;
    if (!uid || !tokenForApi) return;
    if (!activeLobbyName) return;
    if (!questions || questions.length === 0) return;
    if (reportStartSentRef.current) return;
    reportStartSentRef.current = true;
    void pushGameReport({ points: 0, time: 0 });
  }, [loading, uid, tokenForApi, activeLobbyName, questions, pushGameReport]);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resetQuestionUi = useCallback(() => {
    setSelected(new Set());
    setFeedback(null);
    setDisabled5050(new Set());
    setDoubleDipActive(false);
    timeUpHandledRef.current = false;
  }, []);

  const advance = useCallback(
    async (finalAttempts, finalScore) => {
      clearTimer();
      const isLast = currentIndex >= questions.length - 1;
      if (!isLast) {
        setCurrentIndex((p) => p + 1);
        return;
      }
      try {
        await saveLobbyGameProgress({
          token: tokenForApi,
          userId: uid,
          stageId,
          themeName,
          lobby_name: activeLobbyName || lobbyNameMemo,
          total_score: finalScore,
          time_sec: totalElapsedRef.current,
          answers: finalAttempts,
        });
      } catch {
        /* ignore */
      }
      try {
        await pushGameReport({ points: finalScore, time: totalElapsedRef.current });
      } catch {
        /* ignore */
      }
      const correctCount = Array.isArray(finalAttempts) ? finalAttempts.filter((a) => a && a.correct === true).length : 0;
      const totalCount = Array.isArray(questions) ? questions.length : 0;
      clearQuizLifelinesUsed(lifelinesPersistKeyRef.current);
      navigate("/thankyou", {
        replace: true,
        state: {
          lobby_name: activeLobbyName || lobbyNameMemo,
          points: finalScore,
          time: totalElapsedRef.current,
          correct: correctCount,
          total: totalCount,
          isAnchorMode: isAnchorMode === true,
        },
      });
    },
    [
      clearTimer,
      currentIndex,
      questions,
      tokenForApi,
      uid,
      stageId,
      themeName,
      activeLobbyName,
      lobbyNameMemo,
      navigate,
      isAnchorMode,
      pushGameReport,
    ]
  );

  useEffect(() => {
    if (loading || !current) return;
    questionStartRef.current = Date.now();
    resetQuestionUi();
    setRemaining(perQuestionTotalSec);
    clearTimer();
    const q = current;
    intervalRef.current = window.setInterval(() => {
      const elapsed = Math.max(0, Math.floor((Date.now() - questionStartRef.current) / 1000));
      const rem = Math.max(0, perQuestionTotalSec - elapsed);
      setRemaining(rem);
      if (rem <= 0) {
        clearTimer();
        if (timeUpHandledRef.current) return;
        timeUpHandledRef.current = true;
        const elapsedFull = Math.max(0, Math.floor((Date.now() - questionStartRef.current) / 1000));
        const timeTaken = Math.min(Math.max(elapsedFull, perQuestionTotalSec), perQuestionTotalSec);
        totalElapsedRef.current += timeTaken;
        const entry = {
          questionId: q.id,
          question: q.question,
          typed: "",
          correct: false,
          correct_answer: q.correctText,
          timeTakenSec: timeTaken,
          points: 0,
        };
        setAttempts((prev) => {
          const nextAttempts = [...prev, entry];
          void pushGameReport({ points: scoreRef.current, time: totalElapsedRef.current });
          window.setTimeout(() => advance(nextAttempts, scoreRef.current), 1100);
          return nextAttempts;
        });
        setFeedback("timeout");
      }
    }, 250);
    return () => clearTimer();
  }, [loading, currentIndex, current, perQuestionTotalSec, clearTimer, resetQuestionUi, advance, pushGameReport]);

  const processSubmission = useCallback(
    (picksSorted) => {
      if (!current || feedback) return;
      if (doubleDipActive) {
        if (picksSorted.length !== 2) return;
      } else if (picksSorted.length !== 1) {
        return;
      }

      clearTimer();

      const elapsed = Math.max(0, Math.floor((Date.now() - questionStartRef.current) / 1000));
      const timeTaken = Math.min(elapsed, perQuestionTotalSec);
      totalElapsedRef.current += timeTaken;

      const picks = picksSorted;
      const correctIdx = current.answerId - 1;
      const isCorrect = doubleDipActive ? picks.includes(correctIdx) : picks[0] === correctIdx;

      const typedLabel = picks.map((i) => current.options[i]).join(" | ");

      const prior = scoreRef.current;
      const earned = isCorrect ? pointsPerCorrect : 0;
      const nextScore = isCorrect ? prior + earned : prior;
      if (isCorrect) {
        setScore((s) => s + earned);
        fireFeedbackConfetti();
      }
      setFeedback(isCorrect ? "correct" : "wrong");

      const entry = {
        questionId: current.id,
        question: current.question,
        typed: typedLabel,
        correct: isCorrect,
        correct_answer: current.correctText,
        timeTakenSec: timeTaken,
        points: earned,
      };

      setAttempts((prev) => {
        const nextAttempts = [...prev, entry];
        void pushGameReport({ points: nextScore, time: totalElapsedRef.current });
        window.setTimeout(() => {
          advance(nextAttempts, nextScore);
        }, isCorrect ? 1400 : 1100);
        return nextAttempts;
      });
    },
    [
      advance,
      clearTimer,
      current,
      doubleDipActive,
      feedback,
      perQuestionTotalSec,
      pointsPerCorrect,
      pushGameReport,
    ]
  );

  const toggleSelect = (idx0) => {
    if (feedback) return;
    if (disabled5050.has(idx0)) return;

    const needTwo = doubleDipActive;
    const next = new Set(selected);

    if (needTwo && next.size >= 2) return;

    if (next.has(idx0)) {
      next.delete(idx0);
      setSelected(next);
      return;
    }
    if (needTwo) {
      next.add(idx0);
      setSelected(next);
      if (next.size === 2) {
        processSubmission([...next].sort((a, b) => a - b));
      }
      return;
    }
    next.clear();
    next.add(idx0);
    setSelected(next);
    processSubmission([...next].sort((a, b) => a - b));
  };

  const timerRemainingRatio = useMemo(() => {
    if (perQuestionTotalSec <= 0) return 0;
    return Math.min(1, Math.max(0, remaining / perQuestionTotalSec));
  }, [remaining, perQuestionTotalSec]);

  const [timerPulse, setTimerPulse] = useState(false);
  const prevRemainingRef = useRef(null);
  useEffect(() => {
    if (prevRemainingRef.current != null && prevRemainingRef.current !== remaining) {
      setTimerPulse(true);
      const t = window.setTimeout(() => setTimerPulse(false), 380);
      prevRemainingRef.current = remaining;
      return () => window.clearTimeout(t);
    }
    prevRemainingRef.current = remaining;
  }, [remaining]);

  const progressFillPct = useMemo(() => {
    const n = questions.length;
    if (n <= 0) return 0;
    return Math.min(100, ((currentIndex + 1) / n) * 100);
  }, [questions.length, currentIndex]);

  const showLifelinesAside = lifelinesEnabled && (settingsFifty || settingsDouble || settingsFlip);

  const lifelineTipFifty = useMemo(() => {
    let s = "Removes two wrong answers from the four choices. One use per quiz.";
    if (doubleDipActive) s += " Unavailable while Double Dip is active.";
    if (used50) s += " Already used.";
    return s;
  }, [doubleDipActive, used50]);

  const lifelineTipDouble = useMemo(() => {
    let s = "Tap two answers — correct if either is right. One use per quiz.";
    if (usedDouble) s += " Already used.";
    return s;
  }, [usedDouble]);

  const lifelineTipFlip = useMemo(() => {
    let s = "Swaps this question for your reserved backup question. One use per quiz.";
    if (flipSpent) s += " Already used.";
    if (!flipReserve) s += " No reserve in this game.";
    return s;
  }, [flipSpent, flipReserve]);

  const confirmLifeline = useCallback(
    (title, body) =>
      new Promise((resolve) => {
        setLifelineModal({ variant: "confirm", title, body, resolve });
      }),
    []
  );

  const showLifelineInfo = useCallback(
    (title, body) =>
      new Promise((resolve) => {
        setLifelineModal({ variant: "info", title, body, resolve });
      }),
    []
  );

  const onFifty = async () => {
    if (!current || feedback || used50 || !settingsFifty) return;
    if (doubleDipActive) {
      await showLifelineInfo("Not available", "You cannot use 50:50 while Double Dip is active.");
      return;
    }
    const ok = await confirmLifeline("Use 50:50?", "Two wrong answers will be removed.");
    if (!ok) return;
    const wrong = [0, 1, 2, 3].filter((i) => i !== current.answerId - 1);
    const shuffled = shuffleWithSeed(wrong, `${current.id}-50`);
    const hide = new Set(shuffled.slice(0, 2));
    setDisabled5050(hide);
    setUsed50(true);
    if (lifelinesPersistKeyRef.current) writeQuizLifelinesUsed(lifelinesPersistKeyRef.current, { fifty: true });
  };

  const onDouble = async () => {
    if (!current || feedback || usedDouble || !settingsDouble) return;
    const ok = await confirmLifeline(
      "Use Double Dip?",
      "Tap two answers — correct if either is right."
    );
    if (!ok) return;
    setDoubleDipActive(true);
    setUsedDouble(true);
    if (lifelinesPersistKeyRef.current) writeQuizLifelinesUsed(lifelinesPersistKeyRef.current, { double: true });
    setSelected(new Set());
  };

  const onFlip = async () => {
    if (!current || feedback || flipSpent || !settingsFlip || !flipReserve) return;
    const ok = await confirmLifeline("Flip question?", "Swap this question with your reserved one.");
    if (!ok) return;
    const reserve = flipReserve;
    setFlipSpent(true);
    if (lifelinesPersistKeyRef.current) writeQuizLifelinesUsed(lifelinesPersistKeyRef.current, { flip: true });
    setQuestions((prev) => {
      const copy = [...prev];
      copy[currentIndex] = reserve;
      return copy;
    });
    setFlipReserve(current);
  };

  if (loading) {
    return (
      <div className="quiz-ui">
        <div className="quiz-ui__inner" style={{ textAlign: "center", paddingTop: 80, color: "#fff", fontWeight: 800 }}>
          Loading…
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="quiz-ui">
        <div className="quiz-ui__inner" style={{ textAlign: "center", paddingTop: 80, color: "#fff", fontWeight: 800 }}>
          No questions available.
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-ui">
      <div className="quiz-ui__blobs" aria-hidden>
        <div className="quiz-ui__blob" />
        <div className="quiz-ui__blob" />
        <div className="quiz-ui__blob" />
      </div>

      <div className="quiz-ui__inner">
        <header className="quiz-ui__timer-row">
          <span className="quiz-ui__timer-line quiz-ui__timer-line--track quiz-ui__timer-line--track-left">
            <span className="quiz-ui__timer-line-fill" style={{ transform: `scaleX(${timerRemainingRatio})` }} />
          </span>
          <div className={`quiz-ui__timer-box${timerPulse ? " quiz-ui__timer-box--pulse" : ""}`}>{remaining}</div>
          <span className="quiz-ui__timer-line quiz-ui__timer-line--track quiz-ui__timer-line--track-right">
            <span
              className="quiz-ui__timer-line-fill"
              style={{ transform: `scaleX(${timerRemainingRatio})` }}
            />
          </span>
        </header>

        <div className="quiz-ui__layout">
          {showLifelinesAside ? (
            <aside className="quiz-ui__lifelines">
              <div className="quiz-ui__lifelines-header">Lifelines</div>
              <div className="quiz-ui__lifelines-row">
                {settingsFifty ? (
                  <span className="quiz-ui__lifeline-wrap" title={lifelineTipFifty}>
                    <span className="quiz-ui__lifeline-tip" role="tooltip">
                      {lifelineTipFifty}
                    </span>
                    <button
                      type="button"
                      className="quiz-ui__lifeline quiz-ui__lifeline--btn"
                      disabled={!!feedback || used50 || doubleDipActive}
                      onClick={onFifty}
                    >
                      <div className="quiz-ui__lifeline-icon">
                        <img src={fiftyFifty} alt="" />
                      </div>
                      <span>50:50</span>
                    </button>
                  </span>
                ) : null}
                {settingsDouble ? (
                  <span className="quiz-ui__lifeline-wrap" title={lifelineTipDouble}>
                    <span className="quiz-ui__lifeline-tip" role="tooltip">
                      {lifelineTipDouble}
                    </span>
                    <button
                      type="button"
                      className="quiz-ui__lifeline quiz-ui__lifeline--btn"
                      disabled={!!feedback || usedDouble}
                      onClick={onDouble}
                    >
                      <div className="quiz-ui__lifeline-icon">
                        <img src={doubleDip} alt="" />
                      </div>
                      <span>Double Dip</span>
                    </button>
                  </span>
                ) : null}
                {settingsFlip ? (
                  <span className="quiz-ui__lifeline-wrap" title={lifelineTipFlip}>
                    <span className="quiz-ui__lifeline-tip" role="tooltip">
                      {lifelineTipFlip}
                    </span>
                    <button
                      type="button"
                      className="quiz-ui__lifeline quiz-ui__lifeline--btn"
                      disabled={!!feedback || flipSpent || !flipReserve}
                      onClick={onFlip}
                    >
                      <div className="quiz-ui__lifeline-icon">
                        <img src={flip} alt="" />
                      </div>
                      <span>Flip Question</span>
                    </button>
                  </span>
                ) : null}
              </div>
            </aside>
          ) : (
            <div className="quiz-ui__lifelines-spacer" aria-hidden />
          )}

          <main className="quiz-ui__main">
            <div className="quiz-ui__main-stage" key={current.id}>
              <div className="quiz-ui__question">{current.question}</div>
              <div className="quiz-ui__answers">
                {current.options.map((opt, i) => {
                  const grad = ANSWER_GRADIENT[i] || ANSWER_GRADIENT[0];
                  const isSel = selected.has(i);
                  let extra = "";
                  if (feedback === "correct" || feedback === "wrong" || feedback === "timeout") {
                    if (i === current.answerId - 1) extra = " quiz-ui__answer--correct";
                    else if (isSel && i !== current.answerId - 1) extra = " quiz-ui__answer--wrong";
                  } else if (isSel) extra = " quiz-ui__answer--selected";
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`quiz-ui__answer ${grad}${extra}`}
                      disabled={!!feedback || disabled5050.has(i)}
                      onClick={() => toggleSelect(i)}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          </main>

          <aside className="quiz-ui__sidebar-right">
            <div className="quiz-ui__score">
              <span className="quiz-ui__score-label">Your Score</span>
              <span className="quiz-ui__score-value">{score}</span>
            </div>
            <div
              className="quiz-ui__progress-track"
              style={{ ["--quiz-progress-pct"]: `${progressFillPct}%` }}
            >
              <div className="quiz-ui__progress-fill">
                <span className="quiz-ui__progress-num">{currentIndex + 1}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {lifelineModal
        ? createPortal(
            <div
              className="quiz-ui__lifeline-modal-backdrop"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target !== e.currentTarget) return;
                finishLifelineModal(false);
              }}
            >
              <div
                className="quiz-ui__lifeline-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="quiz-ui-lifeline-modal-title"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <h2 id="quiz-ui-lifeline-modal-title" className="quiz-ui__lifeline-modal-title">
                  {lifelineModal.title}
                </h2>
                <p className="quiz-ui__lifeline-modal-body">{lifelineModal.body}</p>
                <div
                  className={`quiz-ui__lifeline-modal-actions${lifelineModal.variant === "info" ? " quiz-ui__lifeline-modal-actions--stack" : ""}`}
                >
                  {lifelineModal.variant === "confirm" ? (
                    <>
                      <button type="button" className="quiz-ui__lifeline-modal-btn quiz-ui__lifeline-modal-btn--ghost" onClick={() => finishLifelineModal(false)}>
                        No
                      </button>
                      <button type="button" className="quiz-ui__lifeline-modal-btn quiz-ui__lifeline-modal-btn--primary" onClick={() => finishLifelineModal(true)}>
                        Yes
                      </button>
                    </>
                  ) : (
                    <button type="button" className="quiz-ui__lifeline-modal-btn quiz-ui__lifeline-modal-btn--primary quiz-ui__lifeline-modal-btn--wide" onClick={() => finishLifelineModal(false)}>
                      OK
                    </button>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
